import React, { useState, useEffect } from 'react';
import { getEntreprise, updateEntreprise, uploadLogo, getPays, BACKEND_URL } from '../api';
import SearchSelect from './SearchSelect';

function Entreprise() {
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
    code_postal: '',
    ville: '',
    telephone: '',
    email: '',
    siren: '',
    siret: '',
    tva_intracommunautaire: '',
    forme_juridique: '',
    capital_social: '',
    conditions_paiement_defaut: '',
    taux_penalites: '',
    clause_penalites: '',
    note_pmt: '',
    note_pmd: '',
    note_aab: '',
    iban: '',
    bic: '',
    site_web: '',
    pays: 'FR',
    auto_entrepreneur: false,
    identifiant_peppol: '',
    identifiant_peppol_scheme: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [paysOptions, setPaysOptions] = useState([]);
  const [logoPreview, setLogoPreview] = useState(null);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadEntreprise();
    getPays().then(r => {
      const sorted = [...r.data].sort((a, b) => a.label.localeCompare(b.label, 'fr'));
      const france = sorted.find(c => c.value === 'FR');
      const rest = sorted.filter(c => c.value !== 'FR');
      setPaysOptions(france ? [france, ...rest] : sorted);
    }).catch(() => {});
  }, []);

  const loadEntreprise = async () => {
    try {
      const response = await getEntreprise();
      if (response.data) {
        const d = response.data;
        setFormData({
          nom: d.nom || '',
          adresse: d.adresse || '',
          code_postal: d.code_postal || '',
          ville: d.ville || '',
          telephone: d.telephone || '',
          email: d.email || '',
          siren: d.siren || '',
          siret: d.siret || '',
          tva_intracommunautaire: d.tva_intracommunautaire || '',
          forme_juridique: d.forme_juridique || '',
          capital_social: d.capital_social || '',
          conditions_paiement_defaut: d.conditions_paiement_defaut || '',
          taux_penalites: d.taux_penalites != null ? String(d.taux_penalites) : '',
          clause_penalites: d.clause_penalites || '',
          note_pmt: d.note_pmt || '',
          note_pmd: d.note_pmd || '',
          note_aab: d.note_aab || '',
          iban: d.iban || '',
          bic: d.bic || '',
          site_web: d.site_web || '',
          pays: d.pays || 'FR',
          auto_entrepreneur: d.auto_entrepreneur === 1,
          identifiant_peppol: d.identifiant_peppol || '',
          identifiant_peppol_scheme: d.identifiant_peppol_scheme || '',
        });
        if (d.logo_url) {
          const logoUrl = d.logo_url.startsWith('http')
            ? d.logo_url
            : `${BACKEND_URL}${d.logo_url}`;
          setLogoPreview(logoUrl);
        }
      }
    } catch (error) {
      console.error('Erreur chargement entreprise:', error);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateEntreprise(formData);
      if (logoFile) {
        const logoResponse = await uploadLogo(logoFile);
        const logoUrl = logoResponse.data.logo_url.startsWith('http')
          ? logoResponse.data.logo_url
          : `${BACKEND_URL}${logoResponse.data.logo_url}`;
        setLogoPreview(logoUrl);
        setLogoFile(null);
      }
      showAlert("Informations de l'entreprise mises à jour avec succès", 'success');
      loadEntreprise();
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de la sauvegarde';
      showAlert(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const field = (label, key, opts = {}) => (
    <div className="form-group">
      <label>{label}</label>
      <input
        type={opts.type || 'text'}
        value={formData[key]}
        onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
        placeholder={opts.placeholder || ''}
        required={opts.required}
      />
      {opts.help && <p className="form-help">{opts.help}</p>}
    </div>
  );

  return (
    <div>
      <h2>Informations de l'entreprise</h2>

      {alert && (
        <div className={`alert alert-${alert.type === 'error' ? 'error' : 'success'}`}>
          {alert.message}
        </div>
      )}

      <div className="card">
        <h3 style={{ marginBottom: '20px' }}>Logo</h3>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', marginBottom: '10px' }}>
          {logoPreview && (
            <img
              src={logoPreview}
              alt="Logo"
              style={{ maxWidth: 120, maxHeight: 120, border: '1px solid var(--border-color)', borderRadius: 4, padding: 8 }}
            />
          )}
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <label>Choisir un logo (JPEG, PNG, GIF — max 5 Mo)</label>
            <input type="file" accept="image/jpeg,image/png,image/gif" onChange={handleFileChange} />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ marginBottom: 20 }}>Identité</h3>

          {field('Nom de l\'entreprise *', 'nom', { required: true, placeholder: 'Raison sociale' })}

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>{field('Forme juridique', 'forme_juridique', { placeholder: 'SAS, SARL, EI…' })}</div>
            <div style={{ flex: 1 }}>{field('Capital social', 'capital_social', { placeholder: '10 000 €' })}</div>
          </div>

          {field('Adresse', 'adresse', { placeholder: 'Numéro et nom de rue' })}

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>{field('Code postal', 'code_postal', { placeholder: '75001' })}</div>
            <div style={{ flex: 2 }}>{field('Ville', 'ville', { placeholder: 'Paris' })}</div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Pays</label>
              <SearchSelect
                options={paysOptions}
                value={formData.pays}
                onChange={(v) => setFormData({ ...formData, pays: v || 'FR' })}
                placeholder="Pays..."
                isClearable={false}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>{field('Téléphone', 'telephone', { type: 'tel', placeholder: '01 23 45 67 89' })}</div>
            <div style={{ flex: 1 }}>{field('Email', 'email', { type: 'email', placeholder: 'contact@entreprise.fr' })}</div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>{field('SIREN', 'siren', { placeholder: '123 456 789' })}</div>
            <div style={{ flex: 1 }}>{field('SIRET', 'siret', { placeholder: '123 456 789 00042' })}</div>
            <div style={{ flex: 1 }}>
              {field('N° TVA intracommunautaire', 'tva_intracommunautaire', { placeholder: 'FR12345678901' })}
            </div>
          </div>

          {field('Site web', 'site_web', { type: 'url', placeholder: 'https://www.entreprise.fr' })}

          <div className="form-group" style={{ marginTop: 20, padding: 15, backgroundColor: 'var(--color-bg)', borderRadius: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.auto_entrepreneur}
                onChange={(e) => setFormData({ ...formData, auto_entrepreneur: e.target.checked })}
                style={{ marginRight: 10, width: 18, height: 18 }}
              />
              <span style={{ fontWeight: 600 }}>Auto-entrepreneur (TVA non applicable, art. 293B CGI)</span>
            </label>
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ marginBottom: 20 }}>Paiement &amp; facturation</h3>

          <div className="form-group">
            <label>Conditions de paiement par défaut</label>
            <textarea
              value={formData.conditions_paiement_defaut}
              onChange={(e) => setFormData({ ...formData, conditions_paiement_defaut: e.target.value })}
              placeholder="Ex : Paiement à 30 jours par virement bancaire"
              rows={2}
              style={{ width: '100%', resize: 'vertical' }}
            />
            <p className="form-help">Pré-remplira chaque nouvelle facture.</p>
          </div>

          {/* ── Clause PDF ── */}
          <div className="form-group">
            <label>Clause pénalités de retard <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontWeight: 400 }}>— affichée sur la facture PDF</span></label>
            <textarea
              value={formData.clause_penalites}
              onChange={(e) => setFormData({ ...formData, clause_penalites: e.target.value })}
              placeholder="En cas de retard de paiement, seront exigibles, conformément au code de commerce, une indemnité calculée sur la base de trois fois le taux de l'intérêt légal en vigueur ainsi qu'une indemnité forfaitaire pour frais de recouvrement de 40 €."
              rows={3}
              style={{ width: '100%', resize: 'vertical' }}
            />
            <p className="form-help">Ce texte s'affiche sur vos factures PDF. Laissez vide pour utiliser la mention légale standard ci-dessus.</p>
          </div>

          {/* ── Notes Factur-X (électronique) ── */}
          <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: '16px 20px', marginBottom: 16 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: 'var(--color-text)' }}>
              Notes Factur-X <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: 12 }}>— obligatoires pour la facturation électronique B2B</span>
            </div>
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 16 }}>
              Ces trois mentions doivent correspondre à votre clause PDF ci-dessus. Laissez vide pour utiliser les valeurs légales par défaut.
            </p>

            <div className="form-group">
              <label>PMT — Frais de recouvrement</label>
              <textarea
                value={formData.note_pmt}
                onChange={(e) => setFormData({ ...formData, note_pmt: e.target.value })}
                placeholder="L'indemnité forfaitaire légale pour frais de recouvrement est de 40 €."
                rows={2}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>

            <div className="form-group">
              <label>PMD — Pénalités de retard</label>
              <textarea
                value={formData.note_pmd}
                onChange={(e) => setFormData({ ...formData, note_pmd: e.target.value })}
                placeholder="En cas de retard de paiement, seront exigibles, conformément au code de commerce, une indemnité calculée sur la base de trois fois le taux de l'intérêt légal en vigueur ainsi qu'une indemnité forfaitaire pour frais de recouvrement de 40 €."
                rows={2}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>AAB — Escompte</label>
              <textarea
                value={formData.note_aab}
                onChange={(e) => setFormData({ ...formData, note_aab: e.target.value })}
                placeholder="Aucun escompte pour paiement anticipé."
                rows={2}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Taux de pénalités personnalisé (%)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={formData.taux_penalites}
              onChange={(e) => setFormData({ ...formData, taux_penalites: e.target.value })}
              placeholder="0"
            />
            <p className="form-help">Optionnel. Si renseigné et que les champs ci-dessus sont vides, ce taux remplace « trois fois le taux légal » dans les textes générés automatiquement.</p>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 2 }}>{field('IBAN', 'iban', { placeholder: 'FR76 3000 6000 0112 3456 7890 189' })}</div>
            <div style={{ flex: 1 }}>{field('BIC', 'bic', { placeholder: 'BNPAFRPPXXX' })}</div>
          </div>

          {field('Identifiant Peppol / Super PDP', 'identifiant_peppol', {
            placeholder: 'Votre SIRET ou identifiant fourni par Super PDP',
            help: 'Adresse de routage électronique pour la transmission via le PPF. Laissez vide pour utiliser votre SIRET automatiquement.',
          })}
          <div className="form-group">
            <label>Schéma de l'identifiant (schemeID)</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              placeholder="0225"
              value={formData.identifiant_peppol_scheme || ''}
              onChange={(e) => setFormData({ ...formData, identifiant_peppol_scheme: e.target.value.replace(/\D/g, '') })}
            />
            <p className="form-help">Vide = 0225 (PPF Annuaire). Utiliser 0224 pour une adresse issue de l'annuaire Chorus Pro.</p>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Sauvegarde…' : 'Enregistrer'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Entreprise;
